'use client';

import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import type { Article, Block } from '@/content/blog/_types';
import AuthorCard from './AuthorCard';
import ShareButtons from './ShareButtons';

interface ArticleRendererProps {
  article: Article;
  url?: string;
  locale?: 'ru' | 'en';
}

function renderBlock(block: Block, index: number) {
  switch (block.type) {
    case 'p':
      return (
        <p key={index} className="text-lg text-slate-600 leading-relaxed mb-6">
          {block.text}
        </p>
      );

    case 'h3':
      return (
        <h3 key={index} className="text-xl font-bold text-slate-900 mb-4">
          {block.text}
        </h3>
      );

    case 'list':
      return block.ordered ? (
        <ol key={index} className="space-y-3 mb-6 text-slate-700 list-decimal list-inside">
          {block.items.map((item, i) => (
            <li key={i} className="ml-4">
              {item}
            </li>
          ))}
        </ol>
      ) : (
        <ul key={index} className="space-y-3 mb-6 text-slate-700">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="text-keiro-green font-bold flex-shrink-0">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );

    case 'table':
      return (
        <div key={index} className="overflow-x-auto mb-8">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-keiro-green">
                {block.headers.map((header, i) => (
                  <th
                    key={i}
                    className="text-left py-3 px-3 font-bold text-slate-900"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIdx) => (
                <tr
                  key={rowIdx}
                  className={rowIdx === block.rows.length - 1 ? 'bg-red-50' : 'border-b border-slate-200'}
                >
                  {row.map((cell, cellIdx) => (
                    <td key={cellIdx} className="py-3 px-3">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'callout':
      const calloutStyles = {
        info: 'bg-slate-50 border-slate-200 text-slate-900',
        warn: 'bg-amber-50 border-amber-200 text-amber-900',
      };
      const style = calloutStyles[block.variant || 'info'];
      return (
        <div
          key={index}
          className={`border rounded-lg p-6 mb-8 ${style}`}
        >
          <p className={block.variant === 'warn' ? 'font-semibold' : ''}>
            {block.text}
          </p>
        </div>
      );

    case 'quote':
      return (
        <div
          key={index}
          className="border-l-4 border-keiro-green bg-keiro-mist/10 p-6 mb-8 rounded-r"
        >
          <p className="text-slate-700 italic mb-3">{block.text}</p>
          {block.author && (
            <p className="text-sm text-slate-600">— {block.author}</p>
          )}
        </div>
      );

    default:
      return null;
  }
}

export default function ArticleRenderer({
  article,
  url = typeof window !== 'undefined' ? window.location.href : '',
  locale = 'ru',
}: ArticleRendererProps) {
  const [, setActiveSection] = useState<string | null>(null);

  const backText = locale === 'en' ? 'Back to blog' : 'Вернуться в блог';

  return (
    <div className="pt-28 pb-20">
      {/* Back Link */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <Link
          href="/blog"
          className="inline-flex items-center gap-2 text-keiro-green hover:text-keiro-green-dark transition-colors"
        >
          <ArrowLeft size={18} />
          <span>{backText}</span>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:col-span-3">
          {/* Hero Section */}
          <section className="mb-16">
            <div className="mb-6">
              <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-keiro-mist text-keiro-green-dark">
                {article.category}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-slate-600">
              <span>{article.displayDate}</span>
              <span>•</span>
              <span>
                {locale === 'en' ? `${article.readingTime}` : `Время чтения: ${article.readingTime}`}
              </span>
            </div>
          </section>

          {/* Author Card */}
          <AuthorCard author={article.author} locale={locale} />

          {/* Table of Contents */}
          <section className="mb-16 bg-slate-50 border border-slate-200 rounded-xl p-8">
            <h2 className="text-xl font-bold text-slate-900 mb-6">
              {locale === 'en' ? 'Table of Contents' : 'Содержание статьи'}
            </h2>
            <ul className="space-y-3">
              {article.sections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveSection(section.id);
                      document
                        .getElementById(section.id)
                        ?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-keiro-green hover:text-keiro-green-dark transition-colors flex items-center gap-2"
                  >
                    <span className="w-2 h-2 bg-keiro-green rounded-full" />
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </section>

          {/* Article Sections */}
          {article.sections.map((section) => (
            <section key={section.id} id={section.id} className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                {section.title}
              </h2>
              {section.blocks.map((block, idx) => renderBlock(block, idx))}
            </section>
          ))}

          {/* Share Buttons */}
          <ShareButtons url={url} title={article.title} />
        </div>
      </div>
    </div>
  );
}
